import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';
import FolderIcon from '@material-ui/icons/FolderOpen';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import ReceiptIcon from '@material-ui/icons/Receipt';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
// Hidden 2026-08 with the Documents stat card -- restore together.
// import DescriptionIcon from '@material-ui/icons/Description';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Close';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import ClientContainer from '../../ClientContainer';
import TutorialPopup from './Tutorial/TutorialPopup';
import StatCard from './StatCard';
import SectionCard from './SectionCard';
import DashboardRowList from './DashboardRowList';
import ProjectDetails from '../Projects/projectDetails';
import { ModalDefaultDraggable } from '../../../../../molecules';

import {
  setShowDrawerContent,
  setShowNewProjectPopup,
  setShowTutorial,
} from '../../../../../redux/slices/generalSlice';
// import { setSelectedDocumentPermit } from '../../../../../redux/slices/documentsSlice';
import { setSelectedPermit } from '../../../../../redux/slices/inspectionsSlice';
import { setSelectedPermit as setSelectedProjectPermit } from '../../../../../redux/slices/projectSlice';
import { setSelectedProjectInvoice } from '../../../../../redux/slices/feesSlice';
import { RootState } from '../../../../../redux/root';
import { ROUTES } from '../../Routes/ClientRoutes';
import {
  useGetAllPermitInspectionsQuery,
  useGetAllInspectionByClientQuery,
} from '../../../../../redux/rtkQuery/apiSlice';
// import { loadDocumentsData } from '../Documents/DocumentsTable';
import useGetCleanRecrods from './DashboardTables/useGetCleanRecrods';
import useGetNewProjectData from '../../../hooks/useGetNewProjectData';
import { Loader } from '../../../../../atoms';

const PAGE_SIZE = 5;

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const dateFormat = (value?: string) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const statusLabel = (inv: any) => {
  if (inv?.paidAt) return 'Paid';
  const due = inv?.dueAt ? new Date(inv.dueAt) : null;
  if (due && !Number.isNaN(due.getTime())) {
    const daysLeft = (due.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    if (daysLeft <= 7) return 'Due Soon';
  }
  return 'Open';
};

function usePagedRows<T>(rows: T[]) {
  const [page, setPage] = useState(0);
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const start = currentPage * PAGE_SIZE;
  const visible = rows.slice(start, start + PAGE_SIZE);
  return {
    visible,
    total,
    start,
    end: start + visible.length,
    currentPage,
    totalPages,
    prev: () => setPage(p => Math.max(0, p - 1)),
    next: () => setPage(p => Math.min(totalPages - 1, p + 1)),
  };
}

const STATS_VISIBLE_KEY = 'client-dashboard:statsVisible';

const readStatsVisible = (): boolean => {
  try {
    const raw = localStorage.getItem(STATS_VISIBLE_KEY);
    if (raw === null) return true;
    return raw !== '0';
  } catch {
    return true;
  }
};

const Dashboard = () => {
  const [statsVisible, setStatsVisible] = useState<boolean>(
    readStatsVisible,
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        STATS_VISIBLE_KEY,
        statsVisible ? '1' : '0',
      );
    } catch {
      /* noop */
    }
  }, [statsVisible]);
  const showTutorial = useSelector(
    (s: RootState) => s.general.showTutorial,
  );
  const info = useSelector(
    (s: RootState) => s.userAuth.info,
  );
  // const vocabulary = useSelector(
  //   (s: RootState) => s.general.vocabulary,
  // );
  // Hidden 2026-08 with the Documents cards below -- restore together.
  // const _docs = useSelector(
  //   (s: RootState) => s.documents.AllPermitsWithDocs,
  // );
  // const docsLoading = useSelector(
  //   (s: RootState) => s.documents.docsLoading,
  // );
  const _inspections = useSelector(
    (s: RootState) => s.inspections.permitInspectionsList,
  );
  const clientProjecPermitList = useSelector(
    (s: RootState) => s.fees.clientProjecPermitList,
  );
  const invoicesLoading = useSelector(
    (s: RootState) => s.fees.invoicesLoading,
  );
  const projects = useSelector(
    (s: RootState) => s.projectsSlice.projects,
  );
  const projectsLoading = useSelector(
    (s: RootState) => s.projectsSlice.projectsLoading,
  );
  const showDrawerContent = useSelector(
    (s: RootState) => s.general.showDrawerContent,
  );
  const selectedProjectPermit = useSelector(
    (s: RootState) => s.projectsSlice.selectedPermit,
  );

  // const docs = useGetCleanRecrods(_docs);
  const inspections = useGetCleanRecrods(_inspections);

  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();

  useGetNewProjectData({
    vocabulary: true,
    documents: true,
    invoices: true,
    inspections: true,
    projects: true,
    activities: true,
  });

  useEffect(() => {
    return () => {
      dispatch(setShowTutorial(false));
    };
  }, []);

  useEffect(() => {
    if (info && !info.loginBefore) {
      dispatch(setShowTutorial(true));
    }
  }, [info]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get('newProject')) {
      const url = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
      window.history.pushState({ path: url }, '', url);
      dispatch(setShowNewProjectPopup(true));
    }
  }, [search]);

  const invoiceRows = useMemo(() => {
    const rows: any[] = [];
    clientProjecPermitList.forEach((prj: any) => {
      const seen = new Set<number>();
      const permits: any[] = [];
      if (prj?.permit && prj.permit.id != null) {
        permits.push(prj.permit);
        seen.add(prj.permit.id);
      }
      (prj?.permits || []).forEach((p: any) => {
        if (p && !seen.has(p.id)) {
          permits.push(p);
          seen.add(p.id);
        }
      });
      permits.forEach((p: any) => {
        (p?.invoices || []).forEach((inv: any) => {
          rows.push({
            id: inv.id,
            permitId: p.id,
            namePermitName: `${prj.projectName}/${p.permitName}`,
            address: p.permitAddress,
            amount: Number(inv.total) || 0,
            dueAt: inv.dueAt,
            status: statusLabel(inv),
          });
        });
      });
    });
    return rows;
  }, [clientProjecPermitList]);

  const projectsCount = clientProjecPermitList.length;
  const inspectionsCount = inspections.length;
  const invoicesCount = invoiceRows.length;

  const projectRows = useMemo(() => {
    const rows = (projects || []).map((p: any) => ({
      ...p,
      namePermitName: p?.namePermitName || p?.name,
      address: p?.address,
      status: p?.status,
      permitId: p?.permitId,
    }));
    return [...rows].sort((a: any, b: any) => {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [projects]);

  const [projectSearch, setProjectSearch] = useState('');

  const filteredProjectRows = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projectRows;
    return projectRows.filter((row: any) => {
      const name = String(row?.namePermitName || '').toLowerCase();
      const address = String(row?.address || '').toLowerCase();
      return name.includes(q) || address.includes(q);
    });
  }, [projectRows, projectSearch]);

  // const docPage = usePagedRows(docs);
  const inspPage = usePagedRows(inspections);
  const invPage = usePagedRows(invoiceRows);
  const projPage = usePagedRows(filteredProjectRows);

  const [pendingInspectionPermit, setPendingInspectionPermit] = useState<number>(-1);
  const { data: inspectionDetail } = useGetAllPermitInspectionsQuery(
    pendingInspectionPermit,
    { skip: pendingInspectionPermit === -1 },
  );

  const {
    isLoading: inspectionsQueryLoading,
    isFetching: inspectionsQueryFetching,
  } = useGetAllInspectionByClientQuery(null);
  const inspectionsLoading =
    (inspectionsQueryLoading || inspectionsQueryFetching) &&
    inspections.length === 0;

  useEffect(() => {
    if (inspectionDetail && pendingInspectionPermit !== -1) {
      const selected = inspections.find(
        (r: any) => r.permitId === pendingInspectionPermit,
      );
      dispatch(
        setSelectedPermit({
          ...selected,
          inspections: inspectionDetail,
        }),
      );
      history.push(`${ROUTES.INSPECTIONS}?openDrawer=true`);
      setPendingInspectionPermit(-1);
    }
  }, [inspectionDetail]);

  // Hidden 2026-08 with the Documents cards below -- restore together.
  // const openDocument = (row: any) => {
  //   const document = docs.find((d: any) => d.permitId === row.permitId);
  //   history.push(`${ROUTES.DOCUMENTS}?openDrawer=true`);
  //   dispatch(setSelectedDocumentPermit(document) as any);
  //   loadDocumentsData(document, dispatch, vocabulary);
  // };

  const openInspection = (row: any) => {
    setPendingInspectionPermit(row.permitId);
  };

  const openInvoice = (row: any) => {
    const projectMatch = clientProjecPermitList.find((r: any) =>
      r?.permit?.id === row.permitId ||
      (r?.permits || []).some((p: any) => p.id === row.permitId),
    );
    if (projectMatch) {
      dispatch(setSelectedProjectInvoice(projectMatch));
    }
    history.push(`${ROUTES.INVOICES}?openDrawer=true`);
  };

  const openProject = (row: any) => {
    dispatch(setSelectedProjectPermit(row));
    dispatch(setShowDrawerContent(true));
  };

  const closeProjectModal = () => dispatch(setShowDrawerContent(false));

  const projectModalTitle = selectedProjectPermit?.name
    ? `${selectedProjectPermit.name}${
        selectedProjectPermit.permitName
          ? ` / ${selectedProjectPermit.permitName}`
          : ''
      }`
    : 'Project Details';

  return (
    <Wrapper>
      <ClientContainer>
        {showTutorial && <TutorialPopup />}
        <DashboardStage>
          <Page>
            <OverviewBar>
              <OverviewLabel>Overview</OverviewLabel>
              <ToggleStatsBtn
                type="button"
                onClick={() => setStatsVisible(v => !v)}
                aria-expanded={statsVisible}
              >
                {statsVisible ? 'Hide' : 'Show'}
                {statsVisible ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </ToggleStatsBtn>
            </OverviewBar>
            {statsVisible && (
            <StatsGrid>
              <StatCard
                icon={<FolderIcon />}
                count={projectsCount}
                label="Projects"
                linkLabel="View all projects"
                to={ROUTES.PROJECTS}
                iconBg="#E9F2FF"
                iconColor="#2563EB"
              />
              <StatCard
                icon={<ReceiptIcon />}
                count={invoicesCount}
                label="Invoices"
                linkLabel="View invoices"
                to={ROUTES.INVOICES}
                iconBg="#F3E8FF"
                iconColor="#9333EA"
              />
              {/* Hidden 2026-08: Documents Needed stat card -- uncomment to restore
                  (and revert StatsGrid to a 4-column track). */}
              {/* <StatCard
                icon={<DescriptionIcon />}
                count={docs.length}
                label="Documents Needed"
                linkLabel="View all documents"
                to={ROUTES.DOCUMENTS}
                iconBg="#FEF3C7"
                iconColor="#B45309"
              /> */}
              <StatCard
                icon={<AssignmentTurnedInIcon />}
                count={inspectionsCount}
                label="Inspections"
                linkLabel="View inspections"
                to={ROUTES.INSPECTIONS}
                iconBg="#DCFCE7"
                iconColor="#16A34A"
              />
            </StatsGrid>
            )}

            <TablesScroll statsVisible={statsVisible}>
            <TwoUp>
              <SectionCard
                title="My Projects"
                icon={<FolderIcon />}
                iconBg="#FEF3C7"
                iconColor="#B45309"
                viewAllTo={ROUTES.PROJECTS}
                toolbar={
                  <SearchField>
                    <SearchIcon />
                    <SearchInput
                      type="text"
                      value={projectSearch}
                      onChange={e => setProjectSearch(e.target.value)}
                      placeholder="Search by project / permit name or address"
                      aria-label="Search projects"
                    />
                    {projectSearch && (
                      <ClearBtn
                        type="button"
                        onClick={() => setProjectSearch('')}
                        aria-label="Clear search"
                      >
                        <ClearIcon />
                      </ClearBtn>
                    )}
                  </SearchField>
                }
                footer={
                  <PagerFooter
                    start={projPage.start}
                    end={projPage.end}
                    total={projPage.total}
                    onPrev={projPage.prev}
                    onNext={projPage.next}
                    disablePrev={projPage.currentPage === 0}
                    disableNext={
                      projPage.currentPage >=
                      projPage.totalPages - 1
                    }
                  />
                }
              >
                <DashboardRowList
                  columns={[
                    {
                      key: 'namePermitName',
                      label: 'Project / Permit Name',
                    },
                    { key: 'address', label: 'Address' },
                    {
                      key: 'status',
                      label: 'Status',
                      width: '130px',
                      render: row => (
                        <ProjectStatusChip status={row.status}>
                          {row.status || '—'}
                        </ProjectStatusChip>
                      ),
                    },
                    {
                      key: 'view',
                      label: 'View',
                      align: 'right',
                      width: '60px',
                      render: () => (
                        <ActionIcon title="View project">
                          <VisibilityIcon />
                        </ActionIcon>
                      ),
                    },
                  ]}
                  rows={projPage.visible}
                  onRowClick={openProject}
                  emptyMessage={
                    projectSearch
                      ? `No projects match "${projectSearch}".`
                      : 'No projects yet.'
                  }
                  isLoading={projectsLoading && projectRows.length === 0}
                />
              </SectionCard>

              <SectionCard
                title="Request Inspections"
                icon={<AssignmentTurnedInIcon />}
                iconBg="#DCFCE7"
                iconColor="#16A34A"
                viewAllTo={ROUTES.INSPECTIONS}
                footer={
                  <PagerFooter
                    start={inspPage.start}
                    end={inspPage.end}
                    total={inspPage.total}
                    onPrev={inspPage.prev}
                    onNext={inspPage.next}
                    disablePrev={inspPage.currentPage === 0}
                    disableNext={
                      inspPage.currentPage >=
                      inspPage.totalPages - 1
                    }
                  />
                }
              >
                <DashboardRowList
                  columns={[
                    {
                      key: 'namePermitName',
                      label: 'Project / Permit Name',
                    },
                    { key: 'address', label: 'Address' },
                    {
                      key: 'request',
                      label: 'Request',
                      align: 'right',
                      width: '70px',
                      render: row =>
                        row.permitId === pendingInspectionPermit ? (
                          <ActionIcon title="Loading…">
                            <Loader size={16} thickness={4} />
                          </ActionIcon>
                        ) : (
                          <ActionIcon title="Request inspection">
                            <CalendarTodayIcon />
                          </ActionIcon>
                        ),
                    },
                  ]}
                  rows={inspPage.visible}
                  onRowClick={openInspection}
                  emptyMessage="No inspections to request."
                  isLoading={inspectionsLoading}
                />
              </SectionCard>
            </TwoUp>

            <TwoUp>
              {/* Hidden 2026-08: Documents Needed section card -- uncomment to restore. */}
              {/*
              <SectionCard
                title="Documents Needed"
                icon={<FolderIcon />}
                iconBg="#E9F2FF"
                iconColor="#2563EB"
                viewAllTo={ROUTES.DOCUMENTS}
                footer={
                  <PagerFooter
                    start={docPage.start}
                    end={docPage.end}
                    total={docPage.total}
                    onPrev={docPage.prev}
                    onNext={docPage.next}
                    disablePrev={docPage.currentPage === 0}
                    disableNext={
                      docPage.currentPage >=
                      docPage.totalPages - 1
                    }
                  />
                }
              >
                <DashboardRowList
                  columns={[
                    {
                      key: 'namePermitName',
                      label: 'Project / Permit Name',
                    },
                    { key: 'address', label: 'Address' },
                    {
                      key: 'view',
                      label: 'View',
                      align: 'right',
                      width: '60px',
                      render: () => (
                        <ActionIcon title="View document">
                          <VisibilityIcon />
                        </ActionIcon>
                      ),
                    },
                  ]}
                  rows={docPage.visible}
                  onRowClick={openDocument}
                  emptyMessage="No documents needed."
                  isLoading={docsLoading && docs.length === 0}
                />
              </SectionCard>
              */}

              <SectionCard
                title="Pay Invoices"
                icon={<ReceiptIcon />}
                iconBg="#F3E8FF"
                iconColor="#9333EA"
                viewAllTo={ROUTES.INVOICES}
                footer={
                  <PagerFooter
                    start={invPage.start}
                    end={invPage.end}
                    total={invPage.total}
                    onPrev={invPage.prev}
                    onNext={invPage.next}
                    disablePrev={invPage.currentPage === 0}
                    disableNext={
                      invPage.currentPage >=
                      invPage.totalPages - 1
                    }
                  />
                }
              >
                <DashboardRowList
                  columns={[
                    {
                      key: 'namePermitName',
                      label: 'Project / Permit Name',
                    },
                    {
                      key: 'amount',
                      label: 'Amount',
                      align: 'center',
                      width: '100px',
                      render: row => (
                        <AmountText>
                          {currencyFormat.format(row.amount)}
                        </AmountText>
                      ),
                    },
                    {
                      key: 'dueAt',
                      label: 'Due',
                      width: '110px',
                      render: row => dateFormat(row.dueAt),
                    },
                    {
                      key: 'status',
                      label: 'Status',
                      align: 'right',
                      width: '100px',
                      render: row => (
                        <StatusChip status={row.status}>
                          {row.status}
                        </StatusChip>
                      ),
                    },
                  ]}
                  rows={invPage.visible}
                  onRowClick={openInvoice}
                  emptyMessage="No invoices to pay."
                  isLoading={invoicesLoading && invoiceRows.length === 0}
                />
              </SectionCard>
            </TwoUp>
            </TablesScroll>
          </Page>
        </DashboardStage>
        {showDrawerContent && (
          <ModalDefaultDraggable
            title={projectModalTitle}
            onClose={closeProjectModal}
            wide
            addContactModal
          >
            <ProjectDetails />
          </ModalDefaultDraggable>
        )}
      </ClientContainer>
    </Wrapper>
  );
};

type PagerProps = {
  start: number;
  end: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  disablePrev: boolean;
  disableNext: boolean;
};

const PagerFooter = ({
  start,
  end,
  total,
  onPrev,
  onNext,
  disablePrev,
  disableNext,
}: PagerProps) => (
  <>
    <PagerInfo>
      Showing {total === 0 ? 0 : start + 1}
      {end > start + 1 ? `-${end}` : ''} of {total}
    </PagerInfo>
    <PagerButtons>
      <PagerBtn
        type="button"
        onClick={onPrev}
        disabled={disablePrev}
        aria-label="Previous page"
      >
        <ChevronLeftIcon />
      </PagerBtn>
      <PagerBtn
        type="button"
        onClick={onNext}
        disabled={disableNext}
        aria-label="Next page"
      >
        <ChevronRightIcon />
      </PagerBtn>
    </PagerButtons>
  </>
);

export default Dashboard;

const Wrapper = styled.div`
  position: relative;
`;

const DashboardStage = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
`;

const Page = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  box-sizing: border-box;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  @media only screen and (max-width: 540px) {
    padding: 16px;
    gap: 16px;
  }
`;


const StatsGrid = styled.div`
  display: grid;
  /* auto-fit rather than a fixed track count: the Documents card is hidden, so
     this must sit right at both 3 and 4 cards without a breakpoint edit. */
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const TwoUp = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(520px, 1fr));
  gap: 20px;

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const TablesScroll = styled.div<{ statsVisible: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  max-height: ${({ statsVisible }) =>
    statsVisible
      ? 'calc(100vh - 320px)'
      : 'calc(100vh - 180px)'};
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: max-height 0.2s ease;

  @media only screen and (max-width: 1024px) {
    max-height: ${({ statsVisible }) =>
      statsVisible
        ? 'calc(100vh - 360px)'
        : 'calc(100vh - 180px)'};
  }

  @media only screen and (max-width: 540px) {
    max-height: ${({ statsVisible }) =>
      statsVisible
        ? 'calc(100vh - 440px)'
        : 'calc(100vh - 200px)'};
  }
`;

const OverviewBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: -4px;
`;

const OverviewLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const ToggleStatsBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid var(--border);
  padding: 4px 10px 4px 12px;
  border-radius: 999px;
  color: var(--neutral-700);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.12s ease,
    border-color 0.12s ease, color 0.12s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
    color: var(--mainText);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  svg {
    font-size: 16px;
  }
`;

const ActionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: var(--primary-600);
  transition: background-color 0.12s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: var(--primary-50);
  }
`;

const SearchField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 280px;
  height: 32px;
  padding: 0 10px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 8px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }

  > svg {
    font-size: 18px;
    color: var(--neutral-500);
    flex-shrink: 0;
  }

  @media only screen and (max-width: 540px) {
    max-width: none;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  color: var(--mainText);
  font-family: inherit;

  &::placeholder {
    color: var(--neutral-400);
  }
`;

const ClearBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease;

  svg {
    font-size: 16px;
  }

  &:hover {
    background: var(--neutral-100);
    color: var(--mainText);
  }
`;

const AmountText = styled.span`
  color: #16a34a;
  font-weight: 600;
`;

const StatusChip = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: ${({ status }) =>
    status === 'Paid'
      ? '#DCFCE7'
      : status === 'Due Soon'
        ? '#FFEDD5'
        : '#DBEAFE'};
  color: ${({ status }) =>
    status === 'Paid'
      ? '#166534'
      : status === 'Due Soon'
        ? '#9A3412'
        : '#1E40AF'};
`;

const projectStatusTone = (status: string) => {
  const s = (status || '').toLowerCase();
  if (/issued|approved|paid|complete/.test(s)) {
    return { bg: '#DCFCE7', fg: '#166534' };
  }
  if (/progress|inspection/.test(s)) {
    return { bg: '#DBEAFE', fg: '#1E40AF' };
  }
  if (/pending|waiting|hold|sent|proposal/.test(s)) {
    return { bg: '#FEF3C7', fg: '#92400E' };
  }
  if (/denied|rejected|overdue|emergency|withdraw/.test(s)) {
    return { bg: '#FEE2E2', fg: '#991B1B' };
  }
  return { bg: 'var(--neutral-100)', fg: 'var(--neutral-700)' };
};

const ProjectStatusChip = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: ${({ status }) => projectStatusTone(status).bg};
  color: ${({ status }) => projectStatusTone(status).fg};
`;

const PagerInfo = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
`;

const PagerButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const PagerBtn = styled.button`
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--neutral-0);
  color: var(--neutral-600);
  cursor: pointer;
  transition: background-color 0.12s ease,
    color 0.12s ease;

  &:hover:not(:disabled) {
    background: var(--neutral-50);
    color: var(--mainText);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    font-size: 16px;
  }
`;
